import { resolveName } from 'components/form'
import { useRef, useState } from 'react'
import { MetaPath } from 'util/metaPath'

/**
 * Hook para gerenciar transições entre campos relacionados em um formulário.
 *
 * Rastreia o estado de transição entre blur e focus de campos dependentes,
 * permitindo controlar comportamentos durante essa janela temporal.
 */
export const useFieldGroupTransition = () => {
  const [activeField, setActiveField] = useState<string | null>(null)
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleFieldBlur = <T>(fieldName: MetaPath<T>) => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
    }

    setActiveField(resolveName(fieldName))

    transitionTimeoutRef.current = setTimeout(() => {
      setActiveField(null)
    }, 100)
  }

  const handleFieldFocus = <T>(fieldName: MetaPath<T>) => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
    }

    setActiveField(resolveName(fieldName))
  }

  const getFieldEvents = <T>(fieldName: MetaPath<T>) => {
    return {
      onFocus: () => handleFieldFocus(fieldName),
      onBlur: () => handleFieldBlur(fieldName),
    }
  }

  const isInTransition = <T>(fieldName: MetaPath<T>) => {
    return activeField !== null && activeField !== resolveName(fieldName)
  }

  return { handleFieldBlur, handleFieldFocus, getFieldEvents, isInTransition }
}
